import { isOpenableUrl, openPendingDocumentTab } from "@/lib/open-document-tab";
import { supabase } from "@/lib/supabase";

/**
 * Reading a stored file reference without a public URL (COL-436).
 *
 * `contracts` and `tenant-documents` are going private. Many rows still hold
 * the full public URL `getPublicUrl` returned at upload time
 * (`…/storage/v1/object/public/<bucket>/<path>`), and that URL stops working
 * the moment a bucket flips. Rather than migrate the data, every reader goes
 * through `resolveStoredFileUrl`: it takes the bucket and path back out of
 * the stored value and mints a short-lived signed URL. Signed URLs work on a
 * public bucket too, so readers can move over before any bucket flips.
 *
 * Mirrored in supabase/functions/_shared/storage-url.ts; Vite and Deno can't
 * share a module. supabase/functions/_shared/storage-url.test.ts runs the
 * same parsing cases against both copies.
 */

export type StorageObjectRef = { bucket: string; path: string };

/** Public, signed and authenticated object URLs all name bucket + path. */
const OBJECT_URL_PATH =
  /^\/storage\/v1\/object\/(?:public|sign|authenticated)\/([^/]+)\/(.+)$/;
const HAS_SCHEME = /^[a-z][a-z\d+.-]*:/i;

function decodeSegment(segment: string): string {
  try {
    return decodeURIComponent(segment);
  } catch {
    return segment; // a stray "%" that isn't an escape: the key is literal
  }
}

/**
 * The bucket and object path behind a Supabase Storage object URL, whatever
 * the host. Dev rows still carry prod-host URLs, so the path decides, not the
 * host. The query string (a signed URL's `?token=`) is dropped. Null for
 * anything that isn't one.
 */
export function parseStorageObjectUrl(value: string): StorageObjectRef | null {
  let pathname: string;
  try {
    pathname = new URL(value).pathname;
  } catch {
    return null;
  }
  const m = OBJECT_URL_PATH.exec(pathname);
  return m ? { bucket: decodeSegment(m[1]), path: decodeSegment(m[2]) } : null;
}

/**
 * Where a stored file value lives: a Storage URL names its own bucket, a bare
 * path lives in `defaultBucket`. Null for any other absolute URL (an external
 * link, a `blob:` preview), which isn't ours to sign.
 */
export function storageRefFor(
  value: string,
  defaultBucket: string
): StorageObjectRef | null {
  const ref = parseStorageObjectUrl(value);
  if (ref) return ref;
  if (HAS_SCHEME.test(value)) return null;
  const path = value.replace(/^\/+/, "");
  return path ? { bucket: defaultBucket, path } : null;
}

/**
 * A browser-openable URL for a stored file value: signed when it points into
 * Storage, unchanged when it is some other http(s)/blob URL. Null when
 * signing fails (missing object, no read access), the value is empty, or the
 * value has a scheme a tab must not open: a stored value is text somebody
 * wrote into a row (`tenant_documents.file_url` is writable by the tenant),
 * so a `javascript:` or `data:` value is refused here, before it can reach an
 * href, a src or `location.href`.
 */
export async function resolveStoredFileUrl(
  value: string,
  defaultBucket: string,
  opts?: { download?: string | boolean; expiresIn?: number }
): Promise<string | null> {
  const ref = storageRefFor(value, defaultBucket);
  if (!ref) {
    if (!HAS_SCHEME.test(value)) return null;
    // Stored values are user data: never hand back `javascript:`/`data:`.
    if (isOpenableUrl(value)) return value;
    console.warn("[storage-url] refused a non-http(s) URL");
    return null;
  }
  const { data, error } = await supabase.storage
    .from(ref.bucket)
    .createSignedUrl(
      ref.path,
      opts?.expiresIn ?? 600,
      opts?.download ? { download: opts.download } : undefined
    );
  if (error || !data?.signedUrl) {
    // The caller only shows "could not open"; the bucket and path say why.
    console.warn("[storage-url] sign failed", ref, error?.message);
    return null;
  }
  return data.signedUrl;
}

/**
 * Open a stored file value in a new tab. The tab opens inside the click
 * (popup blockers) and is pointed at the signed URL once it resolves.
 * Resolves false when the file can't be opened, so the caller can say so in
 * its own words; the blank tab is closed, never left behind.
 */
export async function openStoredFile(
  value: string,
  defaultBucket: string,
  opts?: { download?: string | boolean; expiresIn?: number }
): Promise<boolean> {
  const tab = openPendingDocumentTab();
  try {
    const url = await resolveStoredFileUrl(value, defaultBucket, opts);
    if (url) {
      tab.navigate(url);
      return true;
    }
  } catch {
    // fall through: same outcome as a failed signature
  }
  tab.cancel();
  return false;
}

/**
 * A stored file's contents, for the browser code that stamps or inspects a
 * PDF (the template editors, the invoice stamper). A Storage value is read
 * through the storage API, which works on a private bucket; any other
 * absolute URL is fetched as before. Throws when neither works.
 */
export async function downloadStoredFile(
  value: string,
  defaultBucket: string
): Promise<Blob> {
  const ref = storageRefFor(value, defaultBucket);
  if (ref) {
    const { data, error } = await supabase.storage
      .from(ref.bucket)
      .download(ref.path);
    if (error || !data) {
      throw new Error(
        `Could not download ${ref.bucket}/${ref.path}: ${error?.message ?? "no data"}`
      );
    }
    return data;
  }
  const res = await fetch(value);
  if (!res.ok) throw new Error(`Failed to fetch ${value}: HTTP ${res.status}`);
  return await res.blob();
}

/**
 * Delete a stored file and say whether it is gone. Storage answers a delete it
 * refuses (COL-550: only staff or the uploader may delete) exactly like one
 * with nothing to delete — no error, nothing removed — so an empty result is
 * settled by asking whether the object is still there. True when the file is
 * gone (removed now, or already missing), false when storage refused the
 * delete. Storage errors throw.
 */
export async function removeStoredFile(
  ref: StorageObjectRef
): Promise<boolean> {
  const bucket = supabase.storage.from(ref.bucket);
  const { data, error } = await bucket.remove([ref.path]);
  if (error) throw error;
  if (data?.length) return true;
  const { data: stillThere } = await bucket.exists(ref.path);
  return !stillThere;
}

/**
 * Fetch a stored file's bytes through a freshly signed URL. Used by the
 * checklist gallery to read owner-uploaded proofs (COL-529).
 */
export async function fetchStoredFile(
  value: string,
  defaultBucket: string
): Promise<Response> {
  const url = await resolveStoredFileUrl(value, defaultBucket);
  if (!url) throw new Error(`Could not sign stored file ${value}`);
  return fetch(url);
}
